import { useCallback, useEffect, useRef, useState } from "react";
import { useGetIdentity } from "ra-core";
import {
  dashboardRequest,
  DashboardRequestError,
  getDashboardBaseUrl,
} from "../providers/aws/client";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { MonitorResponse } from "./types";
import { retainNewerEvidence } from "./responseVersion";
import { parseDisplayPolicy } from "./displayPolicy";

type ResourceState<T> = {
  key: string;
  data?: T;
  error?: Error;
  loading: boolean;
};

/** Component-local protected data: never enters the CRM's persisted query cache. */
export function useQueueResource<T extends MonitorResponse>(
  path: string | null,
  mode: "list" | "detail" | "artifact",
) {
  const { data: identity } = useGetIdentity();
  const [refresh, setRefresh] = useState(0);
  const [authVersion, setAuthVersion] = useState(0);
  const [revoked, setRevoked] = useState(false);
  const key = `${identity?.id ?? "anonymous"}:${getDashboardBaseUrl()}:${authVersion}:${path}:${mode === "artifact" ? refresh : "live"}`;
  const [state, setState] = useState<ResourceState<T>>({
    key: "",
    loading: false,
  });
  const latestKey = useRef(key);
  latestKey.current = key;
  const reload = useCallback(() => setRefresh((value) => value + 1), []);

  useEffect(() => {
    const { data } = getSupabaseClient().auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT" || event === "USER_UPDATED") {
        setState({ key: "", loading: false });
        setAuthVersion((value) => value + 1);
        setRevoked(event === "SIGNED_OUT");
      } else if (event === "SIGNED_IN") {
        setRevoked(false);
      }
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!path || identity?.id == null || revoked) {
      setState({ key: "", loading: false });
      return;
    }
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let controller: AbortController | undefined;
    let interval: number | undefined;
    let failures = 0;
    let policyReceivedAt = 0;
    let policy: MonitorResponse["display_policy"] | undefined;
    let denied = false;
    let inFlight = false;
    const fetchOnce = async () => {
      if (disposed || denied || inFlight || document.hidden) return;
      inFlight = true;
      controller = new AbortController();
      setState((previous) => ({
        ...(mode !== "artifact" && previous.key === key ? previous : { key }),
        loading: true,
      }));
      try {
        const data = await dashboardRequest<T>(path, {
          signal: controller.signal,
          cache: "no-store",
        });
        if (disposed || latestKey.current !== key) return;
        if (
          !data ||
          !["stg", "staging"].includes(data.environment) ||
          !data.display_policy
        )
          throw new Error("Invalid Queue response");
        const nextPolicy = parseDisplayPolicy(data.display_policy);
        interval =
          (mode === "list"
            ? nextPolicy.list_refresh_seconds
            : nextPolicy.detail_refresh_seconds) * 1000;
        policy = nextPolicy;
        policyReceivedAt = Date.now();
        failures = 0;
        setState((previous) => ({
          key,
          data: retainNewerEvidence(
            previous.key === key ? previous.data : undefined,
            data,
          ),
          loading: false,
        }));
      } catch (error) {
        if (disposed || controller.signal.aborted || latestKey.current !== key)
          return;
        denied =
          error instanceof DashboardRequestError &&
          [401, 403, 404, 410].includes(error.status);
        failures += 1;
        setState((previous) => ({
          key,
          data:
            !denied && mode !== "artifact" && previous.key === key
              ? previous.data
              : undefined,
          error:
            error instanceof Error ? error : new Error("Queue unavailable"),
          loading: false,
        }));
      } finally {
        inFlight = false;
        if (
          !disposed &&
          !denied &&
          !document.hidden &&
          controller.signal.aborted
        ) {
          // A tab can become visible before the aborted request has settled.
          // Start once it settles, including before the first policy response.
          void fetchOnce();
        } else if (
          !disposed &&
          !denied &&
          !document.hidden &&
          mode !== "artifact" &&
          interval &&
          policy &&
          Date.now() - policyReceivedAt <
            policy.display_policy_max_age_seconds * 1000
        ) {
          const delay = failures
            ? Math.min(
                policy.refresh_error_backoff_max_seconds * 1000,
                interval *
                  policy.refresh_error_backoff_multiplier **
                    Math.min(failures, 10),
              )
            : interval;
          timer = setTimeout(fetchOnce, delay);
        }
      }
    };
    const visibilityChanged = () => {
      clearTimeout(timer);
      if (document.hidden) controller?.abort();
      else void fetchOnce();
    };
    document.addEventListener("visibilitychange", visibilityChanged);
    void fetchOnce();
    return () => {
      disposed = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  }, [path, identity?.id, key, mode, refresh, revoked]);

  const current =
    state.key === key && !revoked
      ? state
      : { key, loading: Boolean(path), data: undefined, error: undefined };
  return { ...current, reload };
}
